/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';
import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';

import type { DropdownMenuItemProps, DropdownMenuProps } from '../../types';
import Icon from '../Icon/Icon';
import './DropdownMenu.less';

// ---------------------------------------------------------------------------
// Context — shares close() so Items can collapse the menu on selection
// ---------------------------------------------------------------------------
const DropdownMenuContext = React.createContext<{ close: () => void }>({ close: () => {} });

// ---------------------------------------------------------------------------
// DropdownMenu.Item
// ---------------------------------------------------------------------------
const Item: React.FC<DropdownMenuItemProps> = ({ onClick, disabled = false, icon, children, className }) => {
  const { close } = React.useContext(DropdownMenuContext);

  return (
    <button
      type="button"
      className={cx('ds-dropdown-menu__item', { 'ds-dropdown-menu__item--disabled': disabled }, className)}
      disabled={disabled}
      onClick={() => {
        if (!disabled) {
          onClick?.();
          close();
        }
      }}>
      {icon && (
        <span className="ds-dropdown-menu__item-icon">
          <Icon name={icon} size="small" />
        </span>
      )}
      {children}
    </button>
  );
};

Item.displayName = 'DropdownMenu.Item';

// ---------------------------------------------------------------------------
// DropdownMenu
// ---------------------------------------------------------------------------
const DropdownMenuImpl: React.FC<DropdownMenuProps> = ({
  trigger,
  children,
  placement = 'bottom-start',
  className,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLSpanElement | null>(null);

  const { refs, floatingStyles } = useFloating({
    placement,
    strategy: 'fixed',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const open = () => setIsOpen(true);
  const close = React.useCallback(() => setIsOpen(false), []);
  const toggle = () => setIsOpen((prev) => !prev);

  // Close on outside click
  React.useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        refs.floating.current &&
        !refs.floating.current.contains(target)
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen, close, refs.floating]);

  // Close on Escape
  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  const setTriggerRef = React.useCallback(
    (node: HTMLSpanElement | null) => {
      triggerRef.current = node;
      refs.setReference(node);
    },
    [refs]
  );

  return (
    <DropdownMenuContext.Provider value={{ close }}>
      {/* Trigger wrapper — transparent span so the caller's Button renders unchanged */}
      <span ref={setTriggerRef} className="ds-dropdown-menu__trigger" onClick={toggle}>
        {trigger}
      </span>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            className={cx('ds-dropdown-menu__popover', className)}
            style={floatingStyles}
            role="menu">
            {children}
          </div>
        </FloatingPortal>
      )}
    </DropdownMenuContext.Provider>
  );
};

DropdownMenuImpl.displayName = 'DropdownMenu';

// Compound component
type DropdownMenuComponent = React.FC<DropdownMenuProps> & { Item: React.FC<DropdownMenuItemProps> };
const DropdownMenu = DropdownMenuImpl as DropdownMenuComponent;
DropdownMenu.Item = Item;

export default DropdownMenu;
